import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { cx, isNilOrEmpty, noop } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import CheckSquare from '@gef-ui/components/atoms/SvgIcon/Icons/CheckSquare';
import Locked from '@gef-ui/components/atoms/SvgIcon/Icons/Locked';
import { getBooleanLabel, getLabelMessage } from '@gef-ui/localization/messages';

import MODALS from 'constants/modals';
import producedProduction from 'features/views/production/common';
import { toggleDocumentToDelete } from 'features/views/production/documents/actions';
import { getIsDocumentToDelete } from 'features/views/production/documents/selectors';
import { setEditMode } from 'features/views/productionNew/actions';
import { equalOrThreeDashes, fromIsoDate } from 'utils';
import { getClientName } from 'utils/client';

const { productionStatuses } = producedProduction.constants;
const { canUserDeleteProduction } = producedProduction.utils;

export const DocumentsTableRow = ({ doc, role, ppDocumentType, isDetailVisible, onDetailVisibilityChange }) => {
	const dispatch = useDispatch();
	const toDelete = useSelector((state) => getIsDocumentToDelete(state, doc.id));

	const handleToggleShowWholeText = useCallback(() => {
		onDetailVisibilityChange();
	}, [onDetailVisibilityChange]);

	const handleDelete = useCallback(() => {
		dispatch(toggleDocumentToDelete(doc.id));
	}, [dispatch, doc.id]);

	const handleOpenClient = useCallback(() => {
		dispatch(setEditMode(true));
		NiceModal.show(MODALS.producedProduction_new, { document: doc });
	}, [dispatch, doc]);

	const generateIconTooltipBasedOnStatus = useCallback((status) => {
		if (!isNilOrEmpty(status)) {
			const result = {};
			switch (productionStatuses[status]) {
				case productionStatuses.VALID:
					result.icon = <CheckSquare />;
					result.color = 'success.main';
					result.tooltip = getLabelMessage('producedProduction:checkSquareTooltip');
					break;
				case productionStatuses.CLOSED:
					result.icon = <Locked />;
					result.color = 'info.main';
					result.tooltip = getLabelMessage('producedProduction:lockedTooltip');
					break;
			}

			const { icon, tooltip, color } = result;
			return (
				<Tooltip title={tooltip}>
					<IconButton disableRipple sx={{ fontSize: '20px', color }}>
						{icon}
					</IconButton>
				</Tooltip>
			);
		}
	}, []);

	const { productionDocStatus, reportedDate, policyNo, documentTypeId, scanned, text = '' } = doc;

	const clientName = getClientName(doc);
	const enableDelete = canUserDeleteProduction(doc, role);
	const documentType = ppDocumentType.find((type) => type.id === documentTypeId);
	const longText = text.length > 10;
	const textSlice = longText ? text.slice(0, 8).concat('...') : text;

	return (
		<>
			<TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
				<TableCell>{generateIconTooltipBasedOnStatus(productionDocStatus)}</TableCell>
				<TableCell>
					<Link underline="none" onClick={handleOpenClient} sx={{ cursor: 'pointer' }}>
						{equalOrThreeDashes(clientName)}
					</Link>
				</TableCell>
				<TableCell>{equalOrThreeDashes(fromIsoDate(reportedDate))}</TableCell>
				<TableCell>{equalOrThreeDashes(policyNo)}</TableCell>
				<TableCell>{equalOrThreeDashes(documentType && documentType.description)}</TableCell>
				<TableCell>{equalOrThreeDashes(getBooleanLabel(scanned))}</TableCell>
				<TableCell className={cx({ Link: longText })} onClick={longText ? handleToggleShowWholeText : noop}>
					{longText ? (
						<Link underline="none" onClick={handleToggleShowWholeText} sx={{ cursor: 'pointer' }}>
							{equalOrThreeDashes(textSlice)}
						</Link>
					) : (
						equalOrThreeDashes(textSlice)
					)}
				</TableCell>
				<TableCell>
					<Checkbox onChange={handleDelete} checked={toDelete} disabled={!enableDelete} />
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={9}>
					<Collapse in={isDetailVisible} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							<TableRow>{text}</TableRow>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

DocumentsTableRow.propTypes = {
	doc: PropTypes.any,
	isDetailVisible: PropTypes.bool,
	onDetailVisibilityChange: PropTypes.func,
	ppDocumentType: PropTypes.array,
	role: PropTypes.string,
};

export default DocumentsTableRow;
